import './index.less';
import classnames from 'classnames';
import { isNumber } from 'lodash';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFramework, useUnit } from '@/framework';
import { useLocaleStore } from '@/i18n/index';
import { parseToUnit } from '@/utils/unit-kit';
import { IExportPlugin, PluginType } from '@/version-types';
import { Displacement } from './displacement';
import { useMouseOperateDisplacement } from './use-mouse-operate-events';

/** 偏移提示 */
const DisplacementView: React.FC = React.memo(() => {
    const framework = useFramework();
    const unit = useUnit();
    const { $t } = useLocaleStore();
    const displacement = framework.service.registration.getInstance<Displacement>('Displacement');
    const [info, setInfo] = useState(() => displacement.getInfo());

    useMouseOperateDisplacement(displacement);

    useEffect(() => displacement.onChange(setInfo), [displacement]);

    if (!info) return null;

    const tipType = info.type;

    const getActualAngle = (angle: number, rotationAngle: number = 0) => {
        let newRotate = (angle + rotationAngle) % 360;
        if (newRotate < 0) {
            newRotate += 360;
        }
        return ~~(newRotate <= 180 ? newRotate : -(360 - newRotate));
    };

    const style = {
        left: `${info.x + 20 + (window.scrollX || 0)}px`,
        top: `${info.y + (window.scrollY || 0)}px`,
    } as React.CSSProperties;

    let mountComponent: React.ReactElement | null = null;
    if (tipType === 'move') {
        const left = info.left as number;
        const top = info.top as number;
        mountComponent = (
            <div className="displacement-tip" style={style}>
                {left > 0 ? $t((v) => v.displaceMent.right) : $t((v) => v.displaceMent.left)}：
                {parseToUnit(Math.abs(left), unit)}
                {unit}
                <br />
                {top > 0 ? $t((v) => v.displaceMent.bottom) : $t((v) => v.displaceMent.top)}：
                {parseToUnit(Math.abs(top), unit)}
                {unit}
            </div>
        );
    } else if (tipType === 'rotate') {
        const rotate = info.rotate as number;
        mountComponent = (
            <div className="displacement-tip" style={style}>
                {$t((v) => v.displaceMent.deg)}：{`${getActualAngle(rotate)}`}°
            </div>
        );
    } else if (tipType === 'custom') {
        mountComponent = (
            <div
                className={classnames('displacement-tip', {
                    'hide-corner': info.hideCorner,
                })}
                style={style}
            >
                {info.txt}
            </div>
        );
    } else if (tipType === 'scale' || tipType === 'resize') {
        const width = info.width as number;
        const height = info.height as number;
        const isMin = info.isMin as boolean;
        const rotate = info.rotate as number;
        if (isMin) {
            mountComponent = (
                <div className="displacement-tip" style={style}>
                    {$t((v) => v.displaceMent.minValue)}
                </div>
            );
        } else {
            mountComponent = (
                <div className="displacement-tip" style={style}>
                    {$t((v) => v.Label_Width)}：{parseToUnit(Math.abs(width), unit)}
                    {unit}
                    <br />
                    {$t((v) => v.Label_Height)}：{parseToUnit(Math.abs(height), unit)}
                    {unit}
                    {isNumber(rotate) ? (
                        <>
                            <br />
                            {$t((v) => v.displaceMent.deg)}：{`${getActualAngle(rotate)}`}°
                        </>
                    ) : null}
                </div>
            );
        }
    }
    return mountComponent ? createPortal(mountComponent, document.body) : null;
});

const exportPlugin: IExportPlugin = {
    type: PluginType.VIEWPORT,
    sort: 1,
    name: 'displacement',
    createInstance({ identity }) {
        return [{ key: 'Displacement', value: new Displacement(identity) }];
    },
    render() {
        return <DisplacementView />;
    },
};

export default exportPlugin;
